import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";

export default function AdminEmailLogs() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState({ status:"all", event:"", recipient:"" });

  const load = () => {
    const params = {};
    if (filter.status !== "all") params.status = filter.status;
    if (filter.event) params.event = filter.event;
    if (filter.recipient) params.recipient = filter.recipient;
    api.get("/admin/email-logs/search", { params }).then(r => setRows(r.data));
    api.get("/admin/email-logs/stats").then(r => setStats(r.data));
  };
  useEffect(() => { load(); }, [filter.status, filter.event]);

  const badge = (s) => {
    const map = { sent:"bg-emerald-900/40 text-emerald-300", failed:"bg-red-900/40 text-red-300",
                  skipped:"bg-slate-700 text-slate-300", queued:"bg-amber-900/40 text-amber-300" };
    return <Badge className={`${map[s]||"bg-slate-700 text-slate-300"} border-none`}>{s}</Badge>;
  };

  return (
    <AdminLayout>
      <h1 className="text-2xl font-bold text-white">Email Logs</h1>
      <p className="text-slate-400 text-sm mt-1">All transactional emails sent from admin@vs-solutions.org.</p>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
          <Stat label="Total" value={stats.total}/>
          <Stat label="Sent" value={stats.sent} tone="emerald"/>
          <Stat label="Failed" value={stats.failed} tone="red"/>
          <Stat label="Skipped" value={stats.skipped} tone="slate"/>
        </div>
      )}

      <Card className="mt-6 p-4 bg-slate-900 border-slate-800">
        <div className="grid md:grid-cols-4 gap-3">
          <Input placeholder="Recipient search" value={filter.recipient} onChange={e=>setFilter({...filter, recipient:e.target.value})} onBlur={load} className="bg-slate-800 border-slate-700 text-white" data-testid="email-filter-recipient"/>
          <Select value={filter.status} onValueChange={v=>setFilter({...filter, status:v})}>
            <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="email-filter-status"><SelectValue/></SelectTrigger>
            <SelectContent>{["all","sent","failed","skipped","queued"].map(x=><SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
          </Select>
          <Input placeholder="Event (e.g. registration_otp)" value={filter.event} onChange={e=>setFilter({...filter, event:e.target.value})} onBlur={load} className="bg-slate-800 border-slate-700 text-white" data-testid="email-filter-event"/>
        </div>
      </Card>

      <Card className="mt-4 p-0 bg-slate-900 border-slate-800 overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="text-slate-400 border-b border-slate-800">
            <tr><Th>Time</Th><Th>Recipient</Th><Th>Subject</Th><Th>Event</Th><Th>Status</Th></tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} className="border-b border-slate-800 hover:bg-slate-800/30" data-testid={`email-row-${r.id}`}>
                <Td>{new Date(r.created_at).toLocaleString()}</Td>
                <Td>{r.to}</Td>
                <Td className="max-w-md truncate">{r.subject}</Td>
                <Td className="text-slate-400">{r.event}</Td>
                <Td>{badge(r.status)}</Td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-slate-500">No email logs match your filters.</td></tr>}
          </tbody>
        </table>
      </Card>
    </AdminLayout>
  );
}

function Stat({ label, value, tone }) {
  const map = { emerald: "text-emerald-300", red: "text-red-300", slate: "text-slate-300" };
  return <div className="p-4 bg-slate-900 border border-slate-800 rounded-md"><div className="text-xs text-slate-400 uppercase tracking-wider">{label}</div><div className={`text-2xl font-bold ${map[tone]||"text-white"} mt-1`}>{value}</div></div>;
}
const Th = ({children}) => <th className="text-left px-4 py-3 font-semibold">{children}</th>;
const Td = ({children, className=""}) => <td className={`px-4 py-3 text-slate-200 ${className}`}>{children}</td>;
